import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { useAuth } from '../contexts/AuthContext.jsx'
import Icon from '../components/Icon.jsx'
import Modal from '../components/Modal.jsx'
import { MultiSelectDropdown, PickerDropdown } from '../components/Dropdown.jsx'
import { CardHead, SettingsRow, WeekdayPicker, Switch, TableSearch } from '../components/SettingsLayout.jsx'
import { ROLES, OWNER_GRANTED_ROLES, roleGuideFor, schedulableStaff } from '../lib/roles.js'
import { toggleInSet } from '../lib/sets.js'
import { useToast } from '../components/Toast.jsx'
// The same rule the server gates the Notify button on — not a copy of it. See shared/scope.js.
import { canNotifyStaff } from '../../../shared/scope.js'
import outlookIcon from '../assets/outlook.webp'
import teamsIcon from '../assets/teams.webp'

// The day-to-day sections, gated on can.administer. Rendered by Settings.jsx, which owns the
// sidebar and the routing — these are just the panes.

// ── People ──────────────────────────────────────────────────────────────────

/**
 * Name, email, roles, and — only when Field staff is one of the selected roles — which job
 * types they're qualified for. Shared by "Add user" and "Edit person": the two are the
 * same form, one of them just starts pre-filled and also offers Disable/Enable.
 */
/**
 * A person's account: who they are, how they sign in, and what they are allowed to do.
 *
 * Job types used to be here too and are not any more. They are not an access question — what
 * somebody is signed off to do is a fact about the crew that changes as people get trained,
 * and it belongs beside their phone number and their leave in the Directory, where whoever
 * knows it is already working. This tab is for login and permissions — and for the title,
 * which the Directory also edits, because the Directory lists the crew and the office has
 * titles too.
 */
function PersonModal({ mode, user, canGrantOwner, onClose, onSaved, onRefresh, roleLabel }) {
  const isEdit = mode === 'edit'
  const [displayName, setDisplayName] = useState(user?.display_name || '')
  const [title, setTitle] = useState(user?.title || '')
  const [email, setEmail] = useState(user?.email || '')
  const [roles, setRoles] = useState(user?.roles || ['field_staff'])
  // Tracked separately from `user`, which is a snapshot taken the moment this modal opened:
  // Enable/Disable below does not close the modal, so the pill and button need their own
  // state to reflect the change immediately rather than waiting for a refetch.
  const [status, setUserStatus] = useState(user?.status)
  const toast = useToast()
  const [busy, setBusy] = useState(false)

  const toggleRole = (r) =>
    setRoles((cur) => (cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r]))

  async function save(e) {
    e.preventDefault()
    setBusy(true)
    try {
      if (isEdit) {
        // Two endpoints, because identity and roles are two different pieces of state
        // server-side. Both are saved together here so "Save changes" reads as one action
        // even though it is not one request.
        await api.patch(`/api/admin/users/${user.id}`, { displayName, email, title: title.trim() || null })
        await api.put(`/api/admin/users/${user.id}/roles`, { roles })
      } else {
        await api.post('/api/admin/users', { displayName, email, roles, title: title.trim() || null })
      }
      onSaved()
    } catch (err) {
      toast.error(err)
      setBusy(false)
    }
  }

  async function setStatus(next) {
    try {
      await api.patch(`/api/admin/users/${user.id}`, { status: next })
      setUserStatus(next)
      // Keeps the table behind the modal in sync without the close-and-refetch `onSaved`
      // triggers — this action is a quick toggle someone flips mid-edit, not a "done here".
      onRefresh?.()
    } catch (err) {
      toast.error(err)
    }
  }

  // Owner and both mode admins are owner-granted — a Team admin handing themselves the Group
  // admin role would make the split between the two boards decorative. The server refuses it
  // regardless (OWNER_GRANTED_ROLES in repos/users.js); this just stops the click.
  const roleOptions = ROLES.filter((r) => !OWNER_GRANTED_ROLES.includes(r) || canGrantOwner)

  return (
    <Modal title={isEdit ? 'Edit person' : 'Add user'} onClose={onClose}>
      <form onSubmit={save}>
        <div className="field">
          <label>
            Name
            {isEdit && status === 'invited' && (
              <span className="tag muted" style={{ marginLeft: 6 }}>Pending login</span>
            )}
            {isEdit && status === 'disabled' && (
              <span className="tag muted" style={{ marginLeft: 6 }}>Disabled</span>
            )}
          </label>
          <input type="text" required autoFocus value={displayName}
            onChange={(e) => setDisplayName(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="person-title">Title <span className="hint">optional</span></label>
          <input id="person-title" type="text" maxLength={60} value={title} placeholder="e.g. Foreman"
            onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div className="field">
          <label>Email (their Microsoft sign-in)</label>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="field">
          <label>Roles</label>
          <MultiSelectDropdown
            value={new Set(roles)}
            onToggle={toggleRole}
            options={roleOptions.map((r) => ({ value: r, label: roleLabel(r) }))}
            placeholder="Select roles"
          />
        </div>
        {roles.includes('field_staff') && (
          <p className="hint" style={{ margin: '-4px 0 4px' }}>
            What this person is signed off to do is set in the Directory, beside their phone
            number and their time off.
          </p>
        )}
        <div className="modal-actions" style={{ marginTop: 20 }}>
          {isEdit && (
            <button
              type="button"
              className="btn"
              onClick={() => setStatus(status === 'disabled' ? 'invited' : 'disabled')}
            >
              {status === 'disabled' ? 'Enable' : 'Disable'}
            </button>
          )}
          <span className="spacer" />
          <button className="btn" type="button" onClick={onClose}>Cancel</button>
          <button className="btn primary" type="submit" disabled={busy}>
            {isEdit ? 'Save changes' : 'Add user'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

const STATUS_FILTERS = [
  { key: 'enabled', label: 'Enabled' },
  { key: 'disabled', label: 'Disabled' },
  { key: 'all', label: 'All' },
]

/**
 * What each role can do, in the tenant's own words for its two modes.
 *
 * Roles are the one setting on this page with consequences nobody can see from the picker —
 * "PMA" and "Group Mode Admin" mean nothing until someone tells you — so the explanation
 * sits one click from where the choice is made rather than in documentation nobody opens.
 * The text lives in lib/roles.js beside the identifiers it describes.
 */
function RoleGuideModal({ onClose, modeName }) {
  const guide = roleGuideFor(modeName('normal'), modeName('cat'))
  return (
    <Modal title="What each role can do" onClose={onClose} width={560}>
      <p className="sub">Users can hold multiple roles.</p>
      <div className="role-guide">
        {guide.map((r) => (
          <div key={r.role} className="role-guide-row">
            <div className="role-guide-head">
              <span className="tag">{r.label}</span>
              <strong>{r.summary}</strong>
            </div>
            <p>{r.detail}</p>
          </div>
        ))}
      </div>
    </Modal>
  )
}

export function People() {
  const { can, roleLabel, modeName } = useAuth()
  const qc = useQueryClient()
  const toast = useToast()
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState(null)
  const [showRoles, setShowRoles] = useState(false)

  const [q, setQ] = useState('')
  const [statusFilter, setStatusFilter] = useState('enabled')
  const [roleFilter, setRoleFilter] = useState(new Set())
  const [selected, setSelected] = useState(new Set())
  const [bulkBusy, setBulkBusy] = useState(false)

  const { data } = useQuery({ queryKey: ['admin-users'], queryFn: () => api.get('/api/admin/users') })
  const refresh = () => qc.invalidateQueries({ queryKey: ['admin-users'] })

  // Opened from the Directory as ?user=<id>: straight into that person's dialog, as a click
  // on their row would. The id comes out of the address once it has been used, so closing
  // the dialog and reloading the page does not open it again.
  const [searchParams, setSearchParams] = useSearchParams()
  const linkedId = searchParams.get('user')
  useEffect(() => {
    if (!linkedId || !data?.users) return
    const person = data.users.find((u) => u.id === linkedId)
    if (person) setEditing(person)
    else toast.error('That person is not in this company any more.')
    const next = new URLSearchParams(searchParams)
    next.delete('user')
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedId, data?.users])

  const toggleRoleFilter = (r) => {
    setRoleFilter((cur) => toggleInSet(cur, r))
    setSelected(new Set())
  }

  const visibleUsers = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return (data?.users || []).filter((u) => {
      if (statusFilter === 'enabled' && u.status === 'disabled') return false
      if (statusFilter === 'disabled' && u.status !== 'disabled') return false
      if (roleFilter.size && !u.roles.some((r) => roleFilter.has(r))) return false
      if (
        needle &&
        !u.display_name.toLowerCase().includes(needle) &&
        !(u.title || '').toLowerCase().includes(needle) &&
        !u.email.toLowerCase().includes(needle)
      ) {
        return false
      }
      return true
    })
  }, [data?.users, q, statusFilter, roleFilter])

  const allVisibleSelected = visibleUsers.length > 0 && visibleUsers.every((u) => selected.has(u.id))
  const toggleSelectAll = () =>
    setSelected(allVisibleSelected ? new Set() : new Set(visibleUsers.map((u) => u.id)))
  const toggleOne = (id) => setSelected((cur) => toggleInSet(cur, id))

  /**
   * One at a time, not Promise.all: disabling the last owner is blocked server-side by
   * counting the OTHER active owners, and two of those checks racing in parallel could both
   * see each other as "the other active owner" and let every owner in the batch through.
   * Sequential requests make each check see the effect of the one before it.
   */
  async function bulkSetStatus(status) {
    setBulkBusy(true)
    const ids = [...selected]
    const failures = []
    for (const id of ids) {
      try {
        await api.patch(`/api/admin/users/${id}`, { status })
      } catch (err) {
        failures.push(err.message)
      }
    }
    if (failures.length) toast.error(failures[0])
    setSelected(new Set())
    setBulkBusy(false)
    refresh()
  }

  return (
    <>
    <div className="card">
      <CardHead
        title="User Management"
        sub={
          <>
            Manage staff, user accounts and roles.{' '}
            <button type="button" className="sub-link" onClick={() => setShowRoles(true)}>
              Learn more about roles
            </button>
          </>
        }
        action={
          <button className="btn primary" onClick={() => setAdding(true)}>
            <Icon name="plus" size={16} /> Add user
          </button>
        }
      />

      {showRoles && <RoleGuideModal onClose={() => setShowRoles(false)} modeName={modeName} />}

      <div className="filter-toolbar">
        <MultiSelectDropdown
          value={roleFilter}
          onToggle={toggleRoleFilter}
          onClear={() => { setRoleFilter(new Set()); setSelected(new Set()) }}
          options={ROLES.map((r) => ({ value: r, label: roleLabel(r) }))}
          placeholder="Roles"
          summary
        />
        <div className="segmented">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key}
              className={statusFilter === f.key ? 'selected' : ''}
              onClick={() => { setStatusFilter(f.key); setSelected(new Set()) }}
            >
              {f.label}
            </button>
          ))}
        </div>
        <TableSearch value={q} onChange={setQ} placeholder="Search name, title or email…" />
      </div>

      <table className="data">
        <thead>
          <tr>
            <th style={{ width: 32 }}>
              <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll} aria-label="Select all" />
            </th>
            <th>Name</th>
            <th>Email</th>
            <th>Roles</th>
          </tr>
        </thead>
        <tbody>
          {visibleUsers.map((u) => (
            <tr key={u.id} className="row-click" onClick={() => setEditing(u)}>
              <td onClick={(e) => e.stopPropagation()}>
                <input type="checkbox" checked={selected.has(u.id)} onChange={() => toggleOne(u.id)} />
              </td>
              <td>
                {u.display_name}
                {u.title && <span style={{ color: 'var(--ink-3)' }}> · {u.title}</span>}
                {/* "Signed in" is not a status worth a whole column — it only matters as a
                    one-off nudge that someone has never actually used their invite yet. */}
                {u.status === 'invited' && <span className="tag muted" style={{ marginLeft: 6 }}>Pending login</span>}
                {u.status === 'disabled' && <span className="tag muted" style={{ marginLeft: 6 }}>Disabled</span>}
              </td>
              <td style={{ color: 'var(--ink-3)' }}>{u.email}</td>
              <td>
                {u.roles.length === 0 && <span className="tag muted">No roles</span>}
                {/* Canonical order (ROLES), not array order — the DB has no opinion on
                    display order and a plain array_agg is not guaranteed stable. */}
                {ROLES.filter((r) => u.roles.includes(r)).map((r) => (
                  <span key={r} className="tag">{roleLabel(r)}</span>
                ))}
              </td>
            </tr>
          ))}
          {visibleUsers.length === 0 && (
            <tr><td colSpan={4} className="empty">No one matches those filters.</td></tr>
          )}
        </tbody>
      </table>
    </div>

    {/* A sibling of the card, not nested inside it: .settings-actions is a sticky bar whose
        containing block is meant to be the whole scrolling pane, per its own definition in
        styles.css. Nested inside the card, its sticky offset was computed against the wrong
        box and rendered on top of the row above it instead of below the table. */}
    {selected.size > 0 && (
      <div className="settings-actions">
        <span className="actions-note">{selected.size} selected</span>
        <button className="btn" disabled={bulkBusy} onClick={() => bulkSetStatus('invited')}>
          Enable
        </button>
        <button className="btn danger" disabled={bulkBusy} onClick={() => bulkSetStatus('disabled')}>
          Disable
        </button>
      </div>
    )}

    {adding && (
      <PersonModal
        mode="create"
        canGrantOwner={can.grantOwner}
        roleLabel={roleLabel}
        onClose={() => setAdding(false)}
        onSaved={() => { setAdding(false); refresh() }}
      />
    )}
    {editing && (
      <PersonModal
        mode="edit"
        user={editing}
        canGrantOwner={can.grantOwner}
        roleLabel={roleLabel}
        onRefresh={refresh}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); refresh() }}
      />
    )}
    </>
  )
}

// ── PM teams ────────────────────────────────────────────────────────────────
// No modal here: attaching an existing person to a PM's default team is not creating a new
// entity, so it stays an inline crew picker like CAT team membership.

export function PmTeams() {
  const qc = useQueryClient()
  const toast = useToast()
  const [q, setQ] = useState('')
  const { data: users } = useQuery({ queryKey: ['admin-users'], queryFn: () => api.get('/api/admin/users') })
  const { data: teams } = useQuery({ queryKey: ['pm-teams'], queryFn: () => api.get('/api/admin/pm-teams') })
  const { data: pairs } = useQuery({ queryKey: ['pm-assistants'], queryFn: () => api.get('/api/admin/pm-assistants') })

  const all = users?.users || []
  const allPms = all.filter((u) => u.roles.includes('pm'))
  const pmas = all.filter((u) => u.roles.includes('pma'))
  const staff = schedulableStaff(all)

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['pm-teams'] })
    qc.invalidateQueries({ queryKey: ['pm-assistants'] })
  }

  const teamOf = (pmId) => (teams?.members || []).filter((m) => m.pm_user_id === pmId).map((m) => m.staff_user_id)
  const assistantOf = (pmId) => (pairs?.pairs || []).find((p) => p.pm_user_id === pmId)?.pma_user_id || ''

  // Matches on the PM's own name or their assistant's — a card is either one, so searching
  // "leah" should surface every PM Leah assists, not just a PM literally named Leah.
  const needle = q.trim().toLowerCase()
  const pms = needle
    ? allPms.filter((pm) => {
        const assistant = pmas.find((p) => p.id === assistantOf(pm.id))
        return pm.display_name.toLowerCase().includes(needle) ||
          (assistant?.display_name || '').toLowerCase().includes(needle)
      })
    : allPms

  async function setAssistant(pmId, pmaId) {
    try {
      await api.put('/api/admin/pm-assistants', { pmUserId: pmId, pmaUserId: pmaId || null })
      refresh()
    } catch (err) { toast.error(err) }
  }

  async function toggleMember(pmId, staffId) {
    const current = teamOf(pmId)
    const next = current.includes(staffId) ? current.filter((s) => s !== staffId) : [...current, staffId]
    try {
      await api.put(`/api/admin/pm-teams/${pmId}`, { staffIds: next })
      refresh()
    } catch (err) { toast.error(err) }
  }

  return (
    <div className="card">
      <CardHead
        title="PM teams"
        sub="Each PM has one default team and can have no more than one assistant. A PMA can support multiple PMs and has the same permissions as the PMs they assist."
      />

      {allPms.length > 0 && (
        <div className="filter-toolbar">
          <TableSearch value={q} onChange={setQ} placeholder="Search PM or assistant…" />
        </div>
      )}

      {allPms.length === 0 && <div className="empty">Nobody has the PM role yet.</div>}
      {allPms.length > 0 && pms.length === 0 && <div className="empty">No PM or assistant matches that.</div>}

      {pms.map((pm) => {
        const members = teamOf(pm.id)
        const available = staff.filter((s) => !members.includes(s.id))
        return (
          <div key={pm.id} className="team-card" style={{ boxShadow: 'none', background: 'var(--fill-1)' }}>
            <div className="team-card-head">
              <span className="team-order"><Icon name="person" size={15} /></span>
              <strong style={{ flex: 1, fontSize: '1rem' }}>{pm.display_name}</strong>
              <label className="hint">Assistant</label>
              <PickerDropdown
                value={assistantOf(pm.id)}
                onPick={(v) => setAssistant(pm.id, v)}
                placeholder="— none —"
                searchable
                options={[
                  { value: '', label: '— none —' },
                  ...pmas.map((p) => ({ value: p.id, label: p.display_name })),
                ]}
              />
            </div>
            <div className="crew">
              {members.length === 0 && <span className="crew-empty">No default team yet.</span>}
              {members.map((id) => {
                const s = staff.find((x) => x.id === id)
                return (
                  <span key={id} className="crew-chip">
                    {s?.display_name || 'Unknown'}
                    <button title="Remove" onClick={() => toggleMember(pm.id, id)}>
                      <Icon name="close" size={12} strokeWidth={2.2} />
                    </button>
                  </span>
                )
              })}
              {available.length > 0 && (
                <PickerDropdown
                  value={null}
                  onPick={(v) => toggleMember(pm.id, v)}
                  placeholder="+ Add crew member…"
                  searchable
                  options={available.map((s) => ({ value: s.id, label: s.display_name }))}
                />
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Job types ───────────────────────────────────────────────────────────────

const JOB_STATUS_FILTERS = [
  { key: 'active', label: 'Active' },
  { key: 'retired', label: 'Retired' },
  { key: 'all', label: 'All' },
]

/** Shared by "Add job type" and "Edit job type" — a name, and — only once one already
 *  exists — Retire/Restore, the same "action in the sticky bar, not its own field" shape
 *  as the person modal's Enable/Disable. */
function JobTypeModal({ mode, jobType, onClose, onSaved, onRefresh }) {
  const isEdit = mode === 'edit'
  const [name, setName] = useState(jobType?.name || '')
  // Tracked locally so Retire/Restore can update the pill without closing the modal —
  // `jobType` is a snapshot from the moment this modal opened.
  const [active, setActiveState] = useState(jobType?.active ?? true)
  const toast = useToast()
  const [busy, setBusy] = useState(false)

  async function save(e) {
    e.preventDefault()
    setBusy(true)
    try {
      if (isEdit) await api.patch(`/api/admin/job-types/${jobType.id}`, { name })
      else await api.post('/api/admin/job-types', { name })
      onSaved()
    } catch (err) {
      toast.error(err)
      setBusy(false)
    }
  }

  async function toggleActive() {
    const next = !active
    try {
      await api.patch(`/api/admin/job-types/${jobType.id}`, { active: next })
      setActiveState(next)
      onRefresh?.()
    } catch (err) {
      toast.error(err)
    }
  }

  return (
    <Modal title={isEdit ? 'Edit job type' : 'Add job type'} onClose={onClose} width={400}>
      <form onSubmit={save}>
        <div className="field">
          <label>
            Name
            {isEdit && !active && <span className="tag muted" style={{ marginLeft: 6 }}>Retired</span>}
          </label>
          <input type="text" required autoFocus placeholder="Framing" value={name}
            onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="modal-actions" style={{ marginTop: 20 }}>
          {/* Retired, never deleted: a board printed in March still has to render. */}
          {isEdit && (
            <button type="button" className="btn" onClick={toggleActive}>
              {active ? 'Retire' : 'Restore'}
            </button>
          )}
          <span className="spacer" />
          <button className="btn" type="button" onClick={onClose}>Cancel</button>
          <button className="btn primary" type="submit" disabled={busy}>
            {isEdit ? 'Save changes' : 'Add job type'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

export function JobTypes() {
  const qc = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState(null)
  const [q, setQ] = useState('')
  const [statusFilter, setStatusFilter] = useState('active')
  const { data } = useQuery({ queryKey: ['admin-job-types'], queryFn: () => api.get('/api/admin/job-types') })
  const refresh = () => qc.invalidateQueries({ queryKey: ['admin-job-types'] })

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return (data?.jobTypes || []).filter((j) => {
      if (statusFilter === 'active' && !j.active) return false
      if (statusFilter === 'retired' && j.active) return false
      if (needle && !j.name.toLowerCase().includes(needle)) return false
      return true
    })
  }, [data?.jobTypes, q, statusFilter])

  return (
    <div className="card">
      <CardHead
        title="Job types"
        sub="Manage job types"
        action={
          <button className="btn primary" onClick={() => setAdding(true)}>
            <Icon name="plus" size={16} /> Add job type
          </button>
        }
      />

      <div className="filter-toolbar">
        <div className="segmented">
          {JOB_STATUS_FILTERS.map((f) => (
            <button
              key={f.key}
              className={statusFilter === f.key ? 'selected' : ''}
              onClick={() => setStatusFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <TableSearch value={q} onChange={setQ} placeholder="Search job types…" />
      </div>

      <table className="data">
        <thead><tr><th>Name</th></tr></thead>
        <tbody>
          {visible.map((j) => (
            <tr key={j.id} className="row-click" onClick={() => setEditing(j)}>
              <td>
                {j.name}
                {!j.active && <span className="tag muted" style={{ marginLeft: 6 }}>Retired</span>}
              </td>
            </tr>
          ))}
          {visible.length === 0 && (
            <tr><td className="empty">No job types match those filters.</td></tr>
          )}
        </tbody>
      </table>

      {adding && (
        <JobTypeModal
          mode="create"
          onClose={() => setAdding(false)}
          onSaved={() => { setAdding(false); refresh() }}
        />
      )}
      {editing && (
        <JobTypeModal
          mode="edit"
          jobType={editing}
          onRefresh={refresh}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh() }}
        />
      )}
    </div>
  )
}

// ── Schedule settings ───────────────────────────────────────────────────────
// Time zone, week start and personal-Microsoft-account access moved to Settings → Configure
// → Operations: they apply company-wide, not just when this mode is running, so they no
// longer belong on a page titled after one mode. See ConfigureSection.jsx.
//
// PM teams renders below, on the same page: a PM's default crew is part of running Team
// mode, not a separate destination, so it lives here rather than behind its own nav item.

export function ScheduleSettings() {
  const { modeName } = useAuth()
  const qc = useQueryClient()
  const toast = useToast()
  const { data } = useQuery({ queryKey: ['admin-settings'], queryFn: () => api.get('/api/admin/settings') })
  const [form, setForm] = useState(null)
  const v = form || data

  if (!v) return <div className="card empty">Loading…</div>

  const set = (patch) => { setForm({ ...v, ...patch }) }

  async function save(e) {
    e.preventDefault()
    try {
      await api.put('/api/admin/settings', {
        normalWorkDays: v.normalWorkDays.map(Number),
        normalSlotCount: Number(v.normalSlotCount),
        normalStaffVisibility: v.normalStaffVisibility,
      })
      toast.success('Saved.')
      // Refetch the settings themselves, not just the session that echoes some of them, and
      // drop the local draft — the save bar reads "Unsaved changes" off exactly that draft,
      // so leaving it behind would keep announcing changes that are already stored.
      qc.invalidateQueries({ queryKey: ['admin-settings'] })
      qc.invalidateQueries({ queryKey: ['me'] })
      setForm(null)
    } catch (err) {
      toast.error(err)
    }
  }

  return (
    <>
    <form className="card" id="normal-schedule" onSubmit={save}>
      <CardHead
        title={`${modeName('normal')} Mode Setup`}
        sub={`These settings will take affect when you are in ${modeName('normal')} mode`}
      />

      <div className="settings-list">
        <SettingsRow label="Working days" hint="Which columns the week view and printouts show.">
          <WeekdayPicker value={v.normalWorkDays} onChange={(d) => set({ normalWorkDays: d })} />
        </SettingsRow>

        <SettingsRow label="Job columns" hint="A day always widens to fit whatever is on it.">
          <input type="number" min="1" max="12" value={v.normalSlotCount}
            onChange={(e) => set({ normalSlotCount: e.target.value })} />
        </SettingsRow>

        <SettingsRow label="Field staff can see">
          <PickerDropdown
            value={v.normalStaffVisibility}
            onPick={(normalStaffVisibility) => set({ normalStaffVisibility })}
            options={[
              { value: 'own', label: 'Only their own jobs' },
              { value: 'team', label: 'Their team' },
              { value: 'all', label: 'The whole board' },
            ]}
          />
        </SettingsRow>
      </div>

    </form>

    <PmTeams />

    {/* Outside the form on purpose — see .settings-actions in styles.css. It floats to the
        foot of the last card on the pane, which is what makes it read as the page's one
        closing action instead of a bar sitting in the middle of two unrelated cards. PM teams
        above saves itself as you edit it; this bar is only ever about the settings card. */}
    <div className="settings-actions">
      {form && <span className="actions-note">Unsaved changes</span>}
      <button className="btn primary" type="submit" form="normal-schedule" disabled={!form}>
        Save changes
      </button>
    </div>
    </>
  )
}

// ── Notifications ───────────────────────────────────────────────────────────

// Outlook is the one channel actually wired up server-side. Teams is a stub: picking it
// swaps the whole pane for a placeholder rather than pretending any of the settings below
// apply to it — nothing here touches the database, on purpose, until it is real.
const NOTIFY_CHANNELS = [
  { key: 'outlook', label: 'Outlook', icon: outlookIcon },
  { key: 'teams', label: 'Teams', icon: teamsIcon },
]

/** Which channel is enabled today (left) and a picker to preview another one (right). */
function ChannelPicker({ value, onChange }) {
  return (
    <div className="channel-status-row">
      <div>
        <div className="hint">Enabled channels</div>
        <div className="channel-enabled-list">
          <img src={outlookIcon} alt="" />
          <span>Outlook</span>
        </div>
      </div>
      <div className="channel-picker">
        {NOTIFY_CHANNELS.map((c) => (
          <button
            key={c.key}
            type="button"
            className={`channel-picker-btn ${value === c.key ? 'selected' : ''}`}
            onClick={() => onChange(c.key)}
          >
            <img src={c.icon} alt="" />
            <span>{c.label}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

/**
 * Who may send the schedule email, and where replies to it go.
 *
 * The From address is shown but not editable: it is one platform mailbox shared by every
 * tenant, and the thing a company actually needs to control is where a crew's reply lands —
 * their own dispatcher, not an unmonitored noreply.
 */
export function NotifySettings() {
  const qc = useQueryClient()
  const toast = useToast()
  const { data } = useQuery({ queryKey: ['notify-settings'], queryFn: () => api.get('/api/admin/notify-settings') })
  const { data: users } = useQuery({ queryKey: ['admin-users'], queryFn: () => api.get('/api/admin/users') })
  const [replyTo, setReplyTo] = useState(null)
  const [search, setSearch] = useState('')
  const [channel, setChannel] = useState('outlook')

  if (!data) return <div className="card empty">Loading…</div>

  const people = (users?.users || []).filter((u) => u.status !== 'disabled')
  const chosen = new Set(data.notifierIds || [])
  const value = replyTo ?? (data.replyTo || '')

  async function saveReplyTo(e) {
    e.preventDefault()
    try {
      await api.put('/api/admin/settings', { notifyReplyTo: value })
      toast.success('Saved.')
      setReplyTo(null)
      qc.invalidateQueries({ queryKey: ['notify-settings'] })
    } catch (err) { toast.error(err) }
  }

  // Saved on the click, like the senders below: two buttons, and a choice that waited for a
  // Save elsewhere on the page would be the one thing on it that did not take.
  async function saveRecipients(recipients) {
    try {
      await api.put('/api/admin/settings', { notifyRecipients: recipients })
      qc.invalidateQueries({ queryKey: ['notify-settings'] })
    } catch (err) { toast.error(err) }
  }

  // Saved per click rather than behind the save bar: this is a list of checkboxes, and a
  // checkbox that does not take effect until you find a button elsewhere on the page is the
  // kind of thing people get wrong in both directions.
  async function toggle(userId) {
    const next = chosen.has(userId)
      ? [...chosen].filter((id) => id !== userId)
      : [...chosen, userId]
    try {
      await api.put('/api/admin/notify-senders', { userIds: next })
      qc.invalidateQueries({ queryKey: ['notify-settings'] })
      qc.invalidateQueries({ queryKey: ['me'] })
    } catch (err) { toast.error(err) }
  }

  const filtered = people.filter((u) =>
    !search.trim() || u.display_name.toLowerCase().includes(search.trim().toLowerCase()),
  )
  // "Can send today", asked of the rule itself rather than of a copy of it. The copy this
  // replaces keyed on OWNER_GRANTED_ROLES, which includes asset_admin — so an asset admin was
  // listed as able to send while the server's fallback (owner or either mode admin) would
  // have refused them the button. One rule, both sides.
  const notifierIds = [...chosen]
  const canSendNow = (u) => canNotifyStaff({ id: u.id, roles: u.roles }, notifierIds)
  const senders = filtered.filter(canSendNow)
  const others = filtered.filter((u) => !canSendNow(u))

  if (channel === 'teams') {
    return (
      <div className="card">
        <ChannelPicker value={channel} onChange={setChannel} />
        <CardHead title="Notifications" sub="Sending through Microsoft Teams." />
        <div className="empty">Still In Development</div>
      </div>
    )
  }

  return (
    <>
      <div className="card">
        <ChannelPicker value={channel} onChange={setChannel} />
      </div>

      <form className="card" id="notify-settings" onSubmit={saveReplyTo}>
        <CardHead
          title="Notifications"
          sub="The “Notify staff” button on the board sends one email covering the day or week on screen, to everybody with work in it."
        />

        {!data.enabled && (
          <div className="notice">
            Sending is not configured on this server yet, so the button stays hidden. These
            settings can still be filled in ready for it.
          </div>
        )}

        <div className="settings-list">
          <SettingsRow label="Sent from" hint="The same address for every company on CrewDispatch Service. Not editable here.">
            <span>{data.from || 'not configured'}</span>
          </SettingsRow>

          <SettingsRow
            label="Replies go to"
            hint="The email address people can reply to."
          >
            <input
              type="email"
              placeholder="dispatch@yourcompany.com"
              value={value}
              onChange={(e) => setReplyTo(e.target.value)}
            />
          </SettingsRow>

          <SettingsRow
            label="Recipients"
            hint={
              data.recipients === 'bcc'
                ? 'Each person gets a blind copy. Nobody sees anyone else’s address, and replies go only to the address above.'
                : 'Everybody is on the CC line. They can see who else got it, and Reply All reaches them all.'
            }
          >
            <div className="segmented" role="group" aria-label="How the crew are addressed">
              {[
                ['cc', 'CC'],
                ['bcc', 'BCC'],
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className={data.recipients === key ? 'selected' : ''}
                  aria-pressed={data.recipients === key}
                  onClick={() => data.recipients !== key && saveRecipients(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </SettingsRow>
        </div>
      </form>

      <div className="card">
        <CardHead
          title="Who can send"
          sub={
            chosen.size
              ? 'Only these people see the Notify staff button.'
              : 'Who is allowed to send notifications to staff'
          }
          action={<TableSearch value={search} onChange={setSearch} placeholder="Search people…" />}
        />
        <div className="settings-list">
          {senders.map((u) => (
            <SettingsRow key={u.id} label={u.display_name} hint={u.email}>
              <Switch checked={chosen.has(u.id)} onChange={() => toggle(u.id)} />
            </SettingsRow>
          ))}
          {senders.length > 0 && others.length > 0 && (
            <div className="settings-list-divider">Everyone else</div>
          )}
          {others.map((u) => (
            <SettingsRow key={u.id} label={u.display_name} hint={u.email}>
              <Switch checked={chosen.has(u.id)} onChange={() => toggle(u.id)} />
            </SettingsRow>
          ))}
          {filtered.length === 0 && <div className="empty">Nobody matches that.</div>}
        </div>
      </div>

      <div className="settings-actions">
        {replyTo !== null && <span className="actions-note">Unsaved changes</span>}
        <button className="btn primary" type="submit" form="notify-settings" disabled={replyTo === null}>
          Save reply address
        </button>
      </div>
    </>
  )
}
